import React, { useState } from "react";
import { Dialog, DialogTitle } from "@headlessui/react";
import tw from "twin.macro";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Footer from "components/footers/FourColumnWithInputForm.js";
import Header from "components/headers/HeaderLight";
import { SectionHeading } from "components/misc/Headings.js";
import { useCatalogs } from "hooks/useCatalogs";

const Container = tw.div`relative`;
const Heading = tw(
  SectionHeading
)`mt-4 font-black text-3xl sm:text-4xl lg:text-5xl text-center leading-tight`;
const PrimaryButton = tw.button`ml-2 px-3 py-1 rounded bg-green-500 text-white font-semibold hover:bg-green-700 transition`;
const Button = tw.button`ml-2 px-3 py-1 rounded bg-primary-500 text-white font-semibold hover:bg-primary-700 transition`;

const AdminCatalogsPage = () => {
  const { categoryOptions: categoryOptionsState, createCategory, updateCategory, deleteCategory, createCatalog } = useCatalogs();
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [catEditMode, setCatEditMode] = useState(false);
  // catForm: { catalogId, categoryId, name }
  const [catForm, setCatForm] = useState({ catalogId: null, categoryId: null, name: "" });
  const [activeCatalogId, setActiveCatalogId] = useState(null); // for modal context

  // Catalog creation modal state
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);

  // Open modal to create a new category for a specific catalog
  const openCatCreateModal = (catalogId) => {
    setCatForm({ catalogId, categoryId: null, name: "" });
    setActiveCatalogId(catalogId);
    setCatEditMode(false);
    setCatModalOpen(true);
  };
  // Open modal to edit a category in a specific catalog
  const openCatEditModal = (catalogId, cat) => {
    setCatForm({ catalogId, categoryId: cat.id, name: cat.name });
    setActiveCatalogId(catalogId);
    setCatEditMode(true);
    setCatModalOpen(true);
  };
  const closeCatModal = () => {
    setCatModalOpen(false);
    setTimeout(() => setCatForm({ catalogId: null, categoryId: null, name: "" }), 200);
  };
  const handleCatChange = (e) => setCatForm({ ...catForm, [e.target.name]: e.target.value });
  const handleCatSubmit = async (e) => {
    e.preventDefault();
    if (catEditMode) {
      await updateCategory({ catalogId: catForm.catalogId, categoryId: catForm.categoryId, name: catForm.name });
    } else {
      await createCategory({ catalogId: catForm.catalogId, name: catForm.name });
    }
    closeCatModal();
  };
  const handleCatDelete = async (catalogId, categoryId) => {
    if (window.confirm("¿Seguro que deseas eliminar esta categoría?")) {
      await deleteCategory({ catalogId, categoryId });
    }
  };

  const handleCreateCatalog = async () => {
    await createCatalog();
    setCatalogModalOpen(false);
  };

  return (
    <AnimationRevealPage>
      <Header />
      <Container tw="py-12">
        <Heading>Catálogos Administrativos</Heading>
        <div className="flex justify-start max-w-4xl mx-auto mb-4 mt-6">
          <PrimaryButton onClick={() => setCatalogModalOpen(true)}>Crear Nuevo Catálogo</PrimaryButton>
        </div>
        {/* Category CRUD Tables for each catalog */}
        <div className="mt-12 flex flex-col items-center mb-12 gap-12">
          {categoryOptionsState.length === 0 ? (
            <div className="w-full max-w-md">
              <h2 className="text-2xl font-bold mb-4 text-center">No hay catálogos registrados.</h2>
            </div>
          ) : (
            // Render two catalogs per row
            Array.from({ length: Math.ceil(categoryOptionsState.length / 2) }, (_, rowIdx) => (
              <div key={rowIdx} className="flex flex-row justify-center gap-8 w-full">
                {categoryOptionsState.slice(rowIdx * 2, rowIdx * 2 + 2).map(catalog => (
                  <div key={catalog.id} className="w-full max-w-md">
                    <h2 className="text-2xl font-bold mb-4 text-center">Catálogo #{catalog.id}</h2>
                    <div className="flex justify-end mb-2">
                      <Button onClick={() => openCatCreateModal(catalog.id)}>Nueva Categoría</Button>
                    </div>
                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="bg-primary-500 text-white">
                            <th className="py-2 px-3 w-12 text-center">ID</th>
                            <th className="py-2 px-3">Nombre</th>
                            <th className="py-2 px-3 w-32 text-center">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {catalog.options.length === 0 ? (
                            <tr><td colSpan={3} className="py-4 text-center">No hay categorías registradas.</td></tr>
                          ) : (
                            catalog.options.map(cat => (
                              <tr key={cat.id} className="border-b last:border-b-0 hover:bg-gray-500">
                                <td className="py-2 px-3 text-center text-gray-700">{cat.id}</td>
                                <td className="py-2 px-3 text-gray-800">{cat.name}</td>
                                <td className="py-2 px-3 text-center">
                                  <button type="button" onClick={() => openCatEditModal(catalog.id, cat)} className="text-blue-600 hover:underline mr-2">Editar</button>
                                  <button type="button" onClick={() => handleCatDelete(catalog.id, cat.id)} className="text-red-500 hover:underline">Eliminar</button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
        {/* Modal for add/edit category */}
        {catModalOpen && (
          <Dialog open={catModalOpen} onClose={closeCatModal} as="div" className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-25" aria-hidden="true" />
            <div className="bg-white rounded-lg p-8 shadow-lg w-full max-w-md z-50 relative">
              <DialogTitle className="text-xl font-bold mb-4">{catEditMode ? "Editar Categoría" : "Nueva Categoría"}</DialogTitle>
              <form onSubmit={handleCatSubmit}>
                <div className="mb-4">
                  <label htmlFor="cat-name" className="block mb-1 font-semibold">Nombre</label>
                  <input id="cat-name" name="name" value={catForm.name} onChange={handleCatChange} required className="w-full px-3 py-2 border rounded" />
                </div>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={closeCatModal} className="ml-2 px-3 py-1 rounded bg-gray-400 hover:bg-gray-600 text-white font-semibold transition">Cancelar</button>
                  <button type="submit" className="ml-2 px-3 py-1 rounded bg-primary-500 text-white font-semibold hover:bg-primary-700 transition">{catEditMode ? "Guardar Cambios" : "Agregar"}</button>
                </div>
              </form>
            </div>
          </Dialog>
        )}
        {/* Modal for add catalog */}
        {catalogModalOpen && (
          <Dialog open={catalogModalOpen} onClose={() => setCatalogModalOpen(false)} as="div" className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-25" aria-hidden="true" />
            <div className="bg-white rounded-lg p-8 shadow-lg w-full max-w-md z-50 relative">
              <DialogTitle className="text-xl font-bold mb-4">Nuevo Catálogo</DialogTitle>
              <div className="mb-4">¿Deseas crear un nuevo catálogo vacío?</div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setCatalogModalOpen(false)} className="ml-2 px-3 py-1 rounded bg-gray-400 hover:bg-gray-600 text-white font-semibold transition">Cancelar</button>
                <button type="button" onClick={handleCreateCatalog} className="ml-2 px-3 py-1 rounded bg-primary-500 text-white font-semibold hover:bg-primary-700 transition">Crear</button>
              </div>
            </div>
          </Dialog>
        )}
      </Container>
      <Footer />
    </AnimationRevealPage>
  );
};

export default AdminCatalogsPage;
